import { Button, Card, Muted, Screen, Stack, color, radius, space, status } from '@biopeak/ui';
import {
  parseDelivery,
  useAcknowledgeAlert,
  useAlerts,
  type DoctorAlert,
} from '../lib/alerts.ts';
import { formatElapsed } from '../lib/format.ts';
import { OPERATOR_SYMBOL, type RuleOperator } from '../lib/alertRules.ts';
import type { TrafficLight } from '../lib/patient.ts';

/**
 * Central de notificações (build-prompt §4.4): "a lista/histórico de alertas
 * recebidos pelo médico e seu status (visto/não visto, canal usado)".
 *
 * Não repete o dado clínico do perfil. Um alerta responde três coisas — qual
 * paciente, o que mudou, e se o aviso chegou — e para o resto existe o perfil,
 * a um clique.
 */

const STATUS_LABEL: Record<string, string> = {
  green: 'Verde',
  yellow: 'Amarelo',
  red: 'Vermelho',
};

const CHANNEL_LABEL: Record<string, string> = {
  panel: 'painel',
  email: 'e-mail',
  whatsapp: 'WhatsApp',
};

export function Notifications({ onOpenPatient }: { onOpenPatient: (id: string) => void }) {
  const alerts = useAlerts();
  const naoVistos = (alerts.data ?? []).filter((a) => a.acknowledged_at === null);

  return (
    <Screen style={{ maxWidth: '1000px' }}>
      <Stack gap={space.xs}>
        <h1 style={{ margin: 0 }}>Notificações</h1>
        <Muted>
          {naoVistos.length === 0
            ? 'Nenhum alerta pendente de revisão.'
            : `${naoVistos.length} ${naoVistos.length === 1 ? 'alerta aguarda' : 'alertas aguardam'} sua revisão.`}
        </Muted>
      </Stack>

      {alerts.isLoading && <Muted>Carregando…</Muted>}

      {alerts.data?.length === 0 && (
        <Card>
          <Stack gap={space.xs}>
            <span style={{ fontWeight: 600, fontSize: '15px' }}>Nenhum alerta ainda</span>
            <Muted style={{ fontSize: '13px' }}>
              Alertas aparecem aqui quando uma regra configurada no perfil de um paciente passa a
              disparar e o estado dele piora.
            </Muted>
          </Stack>
        </Card>
      )}

      <Stack gap={space.md}>
        {(alerts.data ?? []).map((a) => (
          <AlertCard key={a.id} alert={a} onOpenPatient={onOpenPatient} />
        ))}
      </Stack>
    </Screen>
  );
}

function AlertCard({
  alert,
  onOpenPatient,
}: {
  alert: DoctorAlert;
  onOpenPatient: (id: string) => void;
}) {
  const acknowledge = useAcknowledgeAlert();
  const visto = alert.acknowledged_at !== null;
  const para = (alert.status_to ?? 'green') as TrafficLight;
  const tone = status[para];
  const delivery = parseDelivery(alert.delivery);

  return (
    <Card>
      <Stack gap={space.sm}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: space.md,
            flexWrap: 'wrap',
          }}
        >
          <Stack gap={space.xs} style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
              {/* Ponto de estado: o §8.3 permite a paleta de semáforo onde ela
                  carrega informação, e aqui ela É a informação. */}
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: tone.fg,
                  flexShrink: 0,
                }}
              />
              <span style={{ fontSize: '17px', fontWeight: 600 }}>{alert.patient_name}</span>
              <span style={{ fontSize: '15px', color: color.textSecondary }}>
                {STATUS_LABEL[alert.status_from ?? 'green']} →{' '}
                <span style={{ color: tone.fg, fontWeight: 600 }}>{STATUS_LABEL[para]}</span>
              </span>
            </div>

            {alert.metric_name && alert.operator && (
              <Muted style={{ fontSize: '14px' }}>
                {alert.metric_name} {OPERATOR_SYMBOL[alert.operator as RuleOperator]}{' '}
                {alert.threshold_value} {alert.display_unit}
                {/* A janela pertence à frase: "FR durante o sono > 20" lido sem
                    o "nos últimos 7 dias" some com o que distingue tendência de
                    leitura isolada. */}
                {alert.window_minutes
                  ? ` nos últimos ${Math.round(alert.window_minutes / 1440)} dias`
                  : ''}
              </Muted>
            )}

            <Muted style={{ fontSize: '12px' }}>
              {formatElapsed(alert.created_at)}
              {visto && ` · visto ${formatElapsed(alert.acknowledged_at)}`}
            </Muted>
          </Stack>

          <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap' }}>
            <Button
              variant="quiet"
              onClick={() => alert.patient_id && onOpenPatient(alert.patient_id)}
              style={{ fontSize: '14px', padding: '8px 14px' }}
            >
              Abrir perfil
            </Button>
            {!visto && (
              <Button
                disabled={acknowledge.isPending}
                onClick={() => alert.id && acknowledge.mutate(alert.id)}
                style={{ fontSize: '14px', padding: '8px 14px' }}
              >
                {acknowledge.isPending ? 'Marcando…' : 'Marcar como visto'}
              </Button>
            )}
          </div>
        </div>

        {/* Entrega por canal. Fica discreto de propósito: informa quando algo
            não saiu, sem competir com o fato clínico que é o alerta em si. */}
        <div
          style={{
            display: 'flex',
            gap: space.md,
            flexWrap: 'wrap',
            borderTop: `1px solid ${color.border}`,
            paddingTop: space.sm,
          }}
        >
          {Object.entries(delivery).map(([canal, d]) => (
            <span key={canal} style={{ fontSize: '12px', color: color.textSecondary }}>
              {CHANNEL_LABEL[canal] ?? canal}:{' '}
              <span style={{ color: d.status === 'failed' ? status.red.fg : color.textSecondary }}>
                {d.status === 'sent' || d.status === 'acknowledged'
                  ? 'entregue'
                  : d.status === 'pending'
                    ? 'na fila'
                    : 'falhou'}
              </span>
              {d.error && ` — ${d.error}`}
            </span>
          ))}
        </div>
      </Stack>
    </Card>
  );
}

/** Contador discreto para a barra do painel. */
export function AlertBadge({ count, onClick }: { count: number; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        border: `1px solid ${count > 0 ? color.accent : color.border}`,
        background: count > 0 ? `${color.accent}12` : color.white,
        color: count > 0 ? color.accent : color.text,
        borderRadius: radius.pill,
        padding: '6px 14px',
        fontSize: '14px',
        fontWeight: count > 0 ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      Notificações{count > 0 && ` · ${count}`}
    </button>
  );
}
